'use client'

import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import SEO from '@/components/layout/SEO'
import { useTranslation } from 'react-i18next';
import LanguagePicker from '@/components/university/ui/LanguagePicker'

// ── Tokens: black & gold, matches SponsorHub / University aesthetic ──
const M = '#06060c'
const G = '#d4a843'
const BR = 'rgba(212,168,67,0.15)'
const CA = 'rgba(255,255,255,0.03)'
const TX = '#ffffff'
const MU = 'rgba(255,255,255,0.55)'
const serif = "'Source Serif 4', 'Playfair Display', serif"
const sans = "'Inter', system-ui, sans-serif"

export default function SponsorLandingPage() {
  const { t } = useTranslation();

  const navigate = useNavigate()
  const [catalogCount, setCatalogCount] = useState<number | null>(null)
  const [demo, setDemo] = useState<any | null>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Live catalog count, never stale, querying audited catalog classes
  useEffect(() => {
    // What a sponsored child actually opens: academy (hub 0001) + Bible
    // Journey (faith track) + community electives. Excludes the 45 BSc degrees.
    Promise.all([
      supabase.from('classes').select('id', { count: 'exact', head: true })
        .eq('is_active', true)
        .eq('facility_id', '00000000-0000-0000-0000-000000000001'),
      supabase.from('classes').select('id', { count: 'exact', head: true })
        .eq('is_active', true)
        .eq('curriculum_track', 'faith'),
      supabase.from('community_course_library' as any).select('id', { count: 'exact', head: true }),
    ]).then(([a, f, e]) => {
      const ac = (typeof a?.count === 'number' && a.count > 0) ? a.count : 114
      const fc = (typeof f?.count === 'number' && f.count > 0) ? f.count : 9
      const ec = (typeof e?.count === 'number' && e.count > 0) ? e.count : 20
      setCatalogCount(ac + fc + ec)
    }).catch(() => {
      setCatalogCount(114 + 9 + 20)
    })
  }, [])
  
  // Live sample learner, the real demo child (Ava), public read
  useEffect(() => {
    Promise.resolve(
      supabase.from('demo_child_showcase' as any).select('*').limit(1).maybeSingle()
    )
      .then(({ data }: any) => { if (data) setDemo(data) })
      .catch(() => {})
  }, [])

  // Anchor scroll for /sponsor#sample links (escape bar, dashboard empty state)
  useEffect(() => {
    if (window.location.hash === '#sample') {
      document.getElementById('sample')?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  const demoStats = (() => {
    if (!demo) return { lessons: 47, quizAvg: 84, mastery: 72, lessonsPct: 78 }
    const logs = Array.isArray(demo.recent_logs) ? demo.recent_logs : []
    const scored = logs.map((l: any) => Number(l?.metadata?.score ?? 0)).filter((n: any) => n > 0)
    const lessons = logs.length || 47
    const quizAvg = scored.length ? Math.round(scored.reduce((a: number, b: number) => a + b, 0) / scored.length) : 84
    const mastery = Math.round(Number(demo?.enrollments?.[0]?.mastery_score ?? 80)) || 80
    return { lessons, quizAvg, mastery, lessonsPct: Math.min(100, Math.round((lessons / 15) * 100)) }
  })()
  const courses = catalogCount ?? 143  // fallback: 114 academy + 9 Bible Journey + 20 electives

  return (
    <div style={{ background: M, minHeight: '100vh', color: TX, fontFamily: sans }}>
      <SEO
        title="Sponsor a Child's Education : You Pay Half, We Match Half | Still Waters Academy"
        description="Fund a child's complete education for less than your daily coffee. You pay half, we match the rest. Watch their progress on your sponsor dashboard. Real impact, real children, real futures."
      />

      {/* ═══ TOP NAV ═══ */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: 'rgba(6,6,12,0.95)', backdropFilter: 'blur(16px)',
        borderBottom: `1px solid ${BR}`,
        padding: '12px 16px',
      }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', flexShrink: 0 }}>
            <div style={{ width: 34, height: 34, flexShrink: 0 }}>
              <img src="/icons/blue-sage-logo.png" alt="Still Waters" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
            <span style={{ fontFamily: serif, fontWeight: 700, fontSize: 'clamp(15px, 4vw, 18px)', color: TX, whiteSpace: 'nowrap' }}>
              Still Waters <span style={{ color: G }}>Academy</span>
            </span>
          </Link>

          {/* Desktop Right Nav */}
          <div className="hidden md:flex" style={{ alignItems: 'center', gap: 20 }}>
            <LanguagePicker />
            <Link to="/sponsor/dashboard" style={{ color: MU, fontSize: 13, fontWeight: 600, textDecoration: 'none', transition: 'color 0.15s' }}>
              Sponsor Dashboard
            </Link>
            <Link to="/sponsor/give" style={{
              background: G, color: M, padding: '9px 20px', borderRadius: 999,
              fontSize: 13, fontWeight: 800, textDecoration: 'none', transition: 'opacity 0.15s',
            }}>
              Give Now →
            </Link>
          </div>

          {/* Mobile Right Controls */}
          <div className="flex md:hidden" style={{ alignItems: 'center', gap: 8 }}>
            <Link to="/sponsor/give" style={{
              background: G, color: M, padding: '6px 14px', borderRadius: 999,
              fontSize: 12, fontWeight: 800, textDecoration: 'none', whiteSpace: 'nowrap',
            }}>
              Give →
            </Link>
            <button
              onClick={() => setMobileMenuOpen(o => !o)}
              aria-label="Toggle navigation"
              style={{
                width: 38, height: 38, borderRadius: 10,
                border: `1px solid ${BR}`, background: 'rgba(255,255,255,0.06)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: TX, cursor: 'pointer',
              }}
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Drawer */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              style={{ overflow: 'hidden', borderTop: `1px solid ${BR}`, marginTop: 12, paddingTop: 16, paddingBottom: 16 }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Link
                  to="/sponsor/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  style={{ color: TX, fontSize: 15, fontWeight: 600, textDecoration: 'none', padding: '6px 0' }}
                >
                  Sponsor Dashboard
                </Link>
                <div style={{ padding: '8px 0', borderTop: `1px solid ${BR}`, borderBottom: `1px solid ${BR}` }}>
                  <LanguagePicker />
                </div>
                <Link
                  to="/sponsor/give"
                  onClick={() => setMobileMenuOpen(false)}
                  style={{ width: '100%', background: G, color: M, padding: '12px 20px', borderRadius: 12, fontSize: 14, fontWeight: 800, textDecoration: 'none', textAlign: 'center', display: 'block' }}
                >
                  Give Now →
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* ═══ HERO — the emotional core ═══ */}
      <section style={{ position: 'relative', overflow: 'hidden', padding: '120px 24px 80px', textAlign: 'center' }}>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.08, pointerEvents: 'none', background: `radial-gradient(ellipse at 50% 0%, ${G} 0%, transparent 60%)` }} />

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ maxWidth: 800, margin: '0 auto', position: 'relative' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 20px', borderRadius: 999, background: 'rgba(212,168,67,0.1)', border: `1px solid ${BR}`, color: G, fontSize: 11, fontWeight: 800, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 32 }}>
            ❤️ You Pay Half · We Match Half
          </div>

          <h1 style={{ fontFamily: serif, fontSize: 'clamp(2.8rem, 7vw, 5rem)', fontWeight: 700, lineHeight: 1.05, letterSpacing: '-0.02em', margin: '0 0 24px' , color: '#0ea5e9'}}>
            Fund a child's future.<br />
            <span style={{ color: G }}>{t('sponsor.for_less_than_lunch')}</span>
          </h1>

          <p style={{ color: MU, fontSize: 20, lineHeight: 1.7, maxWidth: 620, margin: '0 auto 16px' }}>
            $17.50 a month gives a child in need a complete education: personal AI tutor,
            {` ${courses}+ courses`}, mastery-based learning from primary to graduation.
            You pay half. We match the rest.
          </p>

          <p style={{ color: G, fontSize: 15, fontWeight: 700, margin: '0 auto 48px' }}>
            That's 58 cents a day. One child. One future. Yours to fund.
          </p>

          <motion.button
            whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
            onClick={() => document.getElementById('sponsor-now')?.scrollIntoView({ behavior: 'smooth' })}
            style={{ padding: '22px 52px', borderRadius: 999, background: G, color: M, fontWeight: 800, fontSize: 18, border: 'none', cursor: 'pointer', boxShadow: `0 8px 40px ${G}55`, fontFamily: sans }}
          >
            Sponsor a Child Now →
          </motion.button>
          <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, margin: '20px 0 0' }}>
            One-time or monthly · Cancel anytime · Progress dashboard included
          </p>
        </motion.div>
      </section>

      {/* ═══ THE PAIN — who you're funding, honestly ═══ */}
      <section style={{ padding: '80px 24px', background: '#0a0a12' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <h2 style={{ fontFamily: serif, fontSize: 'clamp(2rem, 5vw, 3.2rem)', fontWeight: 700, margin: '0 0 16px', textAlign: 'center' , color: '#0ea5e9'}}>
            Right now, there's a child who wants to learn
          </h2>
          <p style={{ color: MU, fontSize: 17, textAlign: 'center', maxWidth: 640, margin: '0 auto 60px', lineHeight: 1.7 }}>
            A single parent working two jobs who can't afford tutoring.
            A family displaced by circumstances beyond their control.
            A teenager with the aptitude but not the access.
            They don't need a handout, they need a teacher who won't give up on them.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 }}>
            {[
              { n: '1 in 4', t: 'families can\'t afford any supplemental education for their children' },
              { n: '$25-60', t: 'per HOUR for a human tutor; one lesson costs more than a month of sponsored education' },
              { n: 'Millions', t: 'of bright children whose potential goes unwatered every single year' },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 24, padding: '32px 28px', textAlign: 'center' }}>
                <div style={{ fontFamily: serif, fontSize: 44, color: G, fontWeight: 800, marginBottom: 8 }}>{s.n}</div>
                <div style={{ color: MU, fontSize: 14, lineHeight: 1.5 }}>{s.t}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ THE SOLUTION — how sponsorship works, no jargon ═══ */}
      <section style={{ padding: '80px 24px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <h2 style={{ fontFamily: serif, fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 700, margin: '0 0 48px', textAlign: 'center' , color: '#0ea5e9'}}>
            How you change a life : in three steps
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
            {[
              { n: '01', icon: '💛', t: 'You contribute half', d: `Your $17.50/month covers 50% of a child's full education: every course, every lesson, every quiz, the personal AI tutor.` },
              { n: '02', icon: '🤝', t: 'We match the other half', d: 'Still Waters Academy matches every dollar you give. Your half becomes a whole education, at no compromise to the child.' },
              { n: '03', icon: '🌱', t: 'A child learns. You watch.', d: 'Still Waters Academy matches every dollar you give, then goes further: your half becomes the whole academy, at no compromise to the child.' },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12 }}
                style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 24, padding: '36px 28px' }}>
                <div style={{ fontSize: 40, marginBottom: 16 }}>{s.icon}</div>
                <div style={{ color: G, fontSize: 11, fontWeight: 900, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 8 }}>Step {s.n}</div>
                <h3 style={{ fontFamily: serif, fontSize: 20, fontWeight: 700, margin: '0 0 12px', color: TX }}>{s.t}</h3>
                <p style={{ color: MU, fontSize: 14, lineHeight: 1.65, margin: 0 }}>{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ THE KILLER SECTION — the dashboard nobody else offers ═══ */}
      <section id="sample" style={{ padding: '80px 24px', background: '#0a0a12' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))', gap: 48, alignItems: 'center' }}>
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <h2 style={{ fontFamily: serif, fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 700, margin: '0 0 16px', lineHeight: 1.15 , color: '#0ea5e9'}}>
              Don't just give.<br /><span style={{ color: G }}>{t('sponsor.watch_it_work')}</span>
            </h2>
            <p style={{ color: MU, fontSize: 16, lineHeight: 1.7, margin: '0 0 24px' }}>
              Most charity asks end at the donation. Yours comes with a dashboard.
              Every sponsored child's learning shows up as real data: lessons completed,
              quiz averages, mastery scores climbing month over month.
              This is your 58 cents a day, measured, and the panel beside you is a real sample learner's actual record, not a mockup.
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {[
                'Lessons completed, updated as they learn',
                'Quiz scores and mastery percentage',
                'Anonymised or named; your choice, their dignity',
                'Progress snapshots, never raw data; privacy engineered',
              ].map((f, i) => (
                <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', color: TX, fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
                  <span style={{ color: G, fontWeight: 900 }}>✓</span> {f}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Mock dashboard — visual proof */}
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }}>
            <div style={{ background: '#111118', border: `1px solid ${BR}`, borderRadius: 24, padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 900, color: G, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                    {demo ? 'Sample Learner: Real Data' : 'Sponsor Dashboard'}
                  </div>
                  <div style={{ fontFamily: serif, fontSize: 18, fontWeight: 700, marginTop: 4 }}>
                    {demo ? `${demo.child_name}, age ${demo.child_age}` : 'Sponsored Child · Month 4'}
                  </div>
                </div>
                <span style={{ padding: '4px 12px', borderRadius: 999, background: 'rgba(74,222,128,0.15)', color: '#4ade80', fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>Active</span>
              </div>

              {[
                { l: 'Lessons completed', v: String(demoStats.lessons), pct: demoStats.lessonsPct },
                { l: 'Quiz average', v: `${demoStats.quizAvg}%`, pct: demoStats.quizAvg },
                { l: 'Mastery score', v: `${demoStats.mastery}%`, pct: demoStats.mastery },
              ].map((m, i) => (
                <div key={i} style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ color: MU, fontSize: 12, fontWeight: 600 }}>{m.l}</span>
                    <span style={{ color: G, fontSize: 14, fontWeight: 800 }}>{m.v}</span>
                  </div>
                  <div style={{ height: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 999, overflow: 'hidden' }}>
                    <motion.div initial={{ width: 0 }} whileInView={{ width: `${m.pct}%` }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.3 + i * 0.15 }}
                      style={{ height: '100%', background: `linear-gradient(90deg, ${G}, #f0d78c)`, borderRadius: 999 }} />
                  </div>
                </div>
              ))}

                            {demo && (
                <div style={{ marginTop: 4, marginBottom: 14 }}>
                  <p style={{ fontSize: 10, fontWeight: 900, color: G, letterSpacing: '0.15em', textTransform: 'uppercase', margin: '0 0 8px' }}>{t('sponsor.actual_quiz_history')}</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {(Array.isArray(demo.recent_logs) ? demo.recent_logs.slice(-6).reverse() : []).map((log: any, i: number) => {
                      const score = Number(log?.metadata?.score ?? 0)
                      const retry = Number(log?.metadata?.attempt ?? 1) > 1
                      return (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11.5, color: MU }}>
                          <span>Unit {log?.unit_number ?? '?'} · Lesson {log?.lesson_number ?? '?'}{retry ? ' · retry ✓' : ''}</span>
                          <span style={{ fontWeight: 800, color: score >= 80 ? '#4ade80' : '#fbbf24' }}>{score}%</span>
                        </div>
                      )
                    })}
                  </div>
                  {(() => {
                    const p = demo.global_academic_profile || {}
                    const strengths = (p.strengths || []).slice(0, 3)
                    const struggles = (p.struggles || []).slice(0, 2)
                    if (!strengths.length && !struggles.length) return null
                    return (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
                        {strengths.map((s: string) => (
                          <span key={s} style={{ fontSize: 10, color: '#4ade80', background: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.25)', padding: '3px 9px', borderRadius: 999, fontWeight: 700 }}>{s}</span>
                        ))}
                        {struggles.map((s: string) => (
                          <span key={s} style={{ fontSize: 10, color: '#fbbf24', background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.25)', padding: '3px 9px', borderRadius: 999, fontWeight: 700 }}>{s}</span>
                        ))}
                      </div>
                    )
                  })()}
                </div>
              )}

              <div style={{ borderTop: `1px solid ${BR}`, paddingTop: 14, display: 'flex', justifyContent: 'space-between', fontSize: 11, color: MU }}>
                <span>{demo ? 'Real lesson log: nothing simulated' : 'Last active: Today'}</span>
                <span style={{ color: G, fontWeight: 700 }}>↑ 12% this month</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

            {/* ═══ TRY BEFORE YOU FUND — the proof doors ═══ */}
      <section style={{ padding: '64px 24px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <h2 style={{ fontFamily: serif, fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', fontWeight: 700, margin: '0 0 12px', textAlign: 'center' , color: '#0ea5e9'}}>
            Don't take our word for it: <span style={{ color: G }}>take a lesson</span>
          </h2>
          <p style={{ color: MU, fontSize: 15, textAlign: 'center', maxWidth: 560, margin: '0 auto 40px' }}>
            This is the exact education a sponsored child receives. Try it yourself before you fund a single seat.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 }}>
            {[
                            { icon: '📚', t: 'Browse every course', d: 'The full catalog a sponsored child receives, every module, every lesson, nothing hidden. No account needed.', to: '/sponsor/catalog' },
              { icon: '🎓', t: 'Try a full lesson, free', d: 'Four complete foundation courses with the AI tutor. Browse without an account; a free one starts the lesson.', to: '/prep' },
              { icon: '🌍', t: 'Browse the electives library', d: 'Permaculture, archery, first aid, robotics, the teacher-built courses every sponsored child receives. Free account; your first subject is on us.', to: '/learn/subjects' },
              { icon: '📞', t: 'Walk through it with us', d: "We'll show you a live learner's journey and answer everything before you commit a dollar.", href: 'mailto:support@stillwatersacademy.com?subject=Sponsor walkthrough' },
            ].map((d, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 20, padding: '26px 22px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: 26, marginBottom: 12 }}>{d.icon}</div>
                <h3 style={{ fontSize: 16, fontWeight: 800, margin: '0 0 8px', color: TX }}>{d.t}</h3>
                <p style={{ color: MU, fontSize: 13, lineHeight: 1.6, margin: '0 0 18px', flex: 1 }}>{d.d}</p>
                {d.to
                  ? <Link to={d.to} style={{ color: G, fontWeight: 800, fontSize: 14, textDecoration: 'none' }}>{t('sponsor.start_here')}</Link>
                  : <a href={d.href} style={{ color: G, fontWeight: 800, fontSize: 14, textDecoration: 'none' }}>{t('sponsor.contact_us')}</a>}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ THE PRICING TRUTH — what 58 cents buys ═══ */}
      <section id="sponsor-now" style={{ padding: '80px 24px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontFamily: serif, fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 700, margin: '0 0 12px' , color: '#0ea5e9'}}>
            What your sponsorship includes
          </h2>
          <p style={{ color: MU, fontSize: 16, margin: '0 0 48px' }}>
            Every sponsored child receives the complete platform, nothing held back.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24, textAlign: 'left' }}>
            {[
              { icon: '🎓', t: `${courses}+ courses`, d: 'Standard, Elite Academy, and the full library, every subject from mathematics to music theory' },
              { icon: '🤖', t: 'Personal AI tutor', d: 'One-to-one adaptive teaching, every lesson tailored to their pace, their level, their needs' },
              { icon: '📊', t: 'Mastery-based progression', d: 'They don\'t move on until they\'ve learned it, 80% to pass, retakes without penalty, no child left behind' },
              { icon: '📄', t: 'Printable records', d: 'Real transcripts they can take to any school, employer, or university' },
              { icon: '🌍', t: 'Practical electives library', d: 'Gardening, first aid, archery, robotics, teacher-built courses, growing every month' },
              { icon: '🔒', t: 'Their dignity protected', d: 'Privacy-engineered reporting, sponsors see progress, never personal data' },
            ].map((f, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 20, padding: '24px 22px' }}>
                <div style={{ fontSize: 28, marginBottom: 12 }}>{f.icon}</div>
                <h3 style={{ fontSize: 16, fontWeight: 800, margin: '0 0 8px', color: TX }}>{f.t}</h3>
                <p style={{ color: MU, fontSize: 13, lineHeight: 1.6, margin: 0 }}>{f.d}</p>
              </motion.div>
            ))}
          </div>

          {/* The price anchor */}
          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
            style={{ marginTop: 48, background: `linear-gradient(135deg, rgba(212,168,67,0.1), rgba(212,168,67,0.03))`, border: `1.5px solid ${BR}`, borderRadius: 24, padding: '36px 32px' }}>
            <p style={{ color: G, fontSize: 11, fontWeight: 900, letterSpacing: '0.2em', textTransform: 'uppercase', margin: '0 0 16px' }}>{t('sponsor.the_whole_equation')}</p>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: serif, fontSize: 'clamp(28px, 8vw, 40px)', fontWeight: 800, color: TX }}>$17.50</div>
                <div style={{ color: MU, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>You pay / month</div>
              </div>
              <span style={{ fontSize: 28, color: G, fontWeight: 300 }}>+</span>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: serif, fontSize: 'clamp(28px, 8vw, 40px)', fontWeight: 800, color: G }}>$17.50</div>
                <div style={{ color: MU, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{t('sponsor.we_match')}</div>
              </div>
              <span style={{ fontSize: 28, color: G, fontWeight: 300 }}>=</span>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: serif, fontSize: 'clamp(28px, 8vw, 40px)', fontWeight: 800, color: TX }}>$35</div>
                <div style={{ color: MU, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Full education / month</div>
              </div>
            </div>
            <p style={{ color: MU, fontSize: 13, margin: '20px 0 0' }}>58¢ a day. Less than a coffee. More than most people will ever give.</p>
          </motion.div>
        </div>
      </section>

      {/* ═══ WHO'S SPONSORING — the three doors ═══ */}
      <section style={{ padding: '80px 24px', background: '#0a0a12' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <h2 style={{ fontFamily: serif, fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 700, margin: '0 0 48px', textAlign: 'center' , color: '#0ea5e9'}}>
            However you give, it works
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
            {[
              { icon: '💛', t: 'Individuals', d: 'Fund one child personally. Watch their progress. Leave a dedication message, for a birthday, in memory, in hope.', cta: 'Sponsor a child', go: '/sponsor/give' },
              { icon: '🏢', t: 'Businesses', d: 'CSR that photographs well. Fund 10 or 20 students, impact dashboards for your annual report, social tags, logo on the story.', cta: 'Fund a classroom', go: '/sponsor/corporate' },
              { icon: '🌍', t: 'NGOs & Churches', d: 'Distribute education through your community. We handle the platform; you handle the people. Zero cost to your families.', cta: 'Partner with us', go: '/ngo/apply' },
            ].map((d, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 24, padding: '32px 28px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: 36, marginBottom: 16 }}>{d.icon}</div>
                <h3 style={{ fontFamily: serif, fontSize: 22, fontWeight: 700, margin: '0 0 12px' , color: '#0ea5e9'}}>{d.t}</h3>
                <p style={{ color: MU, fontSize: 14, lineHeight: 1.65, margin: '0 0 24px', flex: 1 }}>{d.d}</p>
                <Link to={d.go} style={{ color: G, fontWeight: 800, fontSize: 14, textDecoration: 'none' }}>
                  {d.cta} →
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SOCIAL PROOF PLACEHOLDER — the pilot stories will live here ═══ */}
      <section style={{ padding: '80px 24px' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontFamily: serif, fontSize: 'clamp(2rem, 4vw, 2.8rem)', fontWeight: 700, margin: '0 0 16px' , color: '#0ea5e9'}}>
            Why we built this
          </h2>
          <blockquote style={{ fontFamily: serif, fontSize: 22, fontStyle: 'italic', color: TX, lineHeight: 1.6, margin: '0 0 16px', borderLeft: `3px solid ${G}`, paddingLeft: 24, textAlign: 'left' }}>
            "Every child deserves a teacher who never gives up on them. We couldn't hire enough humans, so we built one that never sleeps, never tires, and never stops believing a child can learn."
          </blockquote>
          <p style={{ color: MU, fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', textAlign: 'left', paddingLeft: 27 }}>
            The Still Waters Academy team
          </p>
          {/* NOTE: replace this block with real sponsor + child stories as the pilot generates them.
              Receipted format: photo + quote + "X months sponsored" + progress delta. */}
        </div>
      </section>

      {/* ═══ FAQ — the objections your Bali/Australia network will raise ═══ */}
      <section style={{ padding: '80px 24px', background: '#0a0a12' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <h2 style={{ fontFamily: serif, fontSize: 'clamp(2rem, 4vw, 2.8rem)', fontWeight: 700, margin: '0 0 48px', textAlign: 'center' , color: '#0ea5e9'}}>
            Questions smart sponsors ask
          </h2>
          {[
            { q: 'How do I know my money reaches a real child?', a: 'Every seat is assigned through verified hardship applications and vetted NGO partnerships. Your dashboard shows a real child\'s real progress: lessons, scores, dates. You can cancel any month and see exactly what your last payment funded.' },
            { q: 'How does the 50% match work?', a: 'You pay $17.50; we contribute more than the other half. Your seat funds a child\'s standard education, and our match completes it and unlocks the entire academy: Elite Academy, Bible Journey, and the practical electives, at no extra cost to you or the child.' },
            { q: 'What does the child actually receive?', a: `The complete platform: ${courses}+ courses, a personal AI tutor that adapts to their level and pace, mastery-based quizzes, printable transcripts, and practical electives from gardening to robotics. Nothing is held back from sponsored children.` },
            { q: 'Can I choose the child?', a: 'Both ways are supported. In Pay-It-Forward mode, our team assigns your seat to the child with the greatest verified need, you watch their anonymised progress. In Named Seat Sponsorship, you fund a child you choose, a nephew overseas, your housekeeper\'s daughter, a family from your church, and we onboard them directly: invitation, their own login, and their full learning journey on your dashboard. Most individuals start named; most businesses run pool.' },
            { q: 'What if I need to stop?', a: 'Cancel anytime, monthly or mid-term. The child\'s access continues to the end of the period you\'ve paid for, and their seat passes to another sponsor from the pool. No guilt, no lock-in.' },
            { q: 'Is my sponsorship tax-deductible?', a: 'Individual sponsorships may qualify depending on your jurisdiction\'s rules on educational gifts. Business sponsorships through our CSR program are structured for deductible giving, contact us for documentation.' },
            { q: 'How is this different from other education charities?', a: 'Three ways: (1) 100% of your sponsorship funds education; no buildings, no admin overhead eating your gift. (2) You see measurable learning outcomes, not just a thank-you letter. (3) The education is genuinely world-class: the same platform paying families use, at full quality, nothing diluted for the sponsored child.' },
          ].map((f, i) => (
            <FaqItem key={i} q={f.q} a={f.a} defaultOpen={i === 0} />
          ))}
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section style={{ padding: '100px 24px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.12, pointerEvents: 'none', background: `radial-gradient(ellipse at center, ${G} 0%, transparent 65%)` }} />
        <div style={{ maxWidth: 640, margin: '0 auto', position: 'relative' }}>
          <h2 style={{ fontFamily: serif, fontSize: 'clamp(2.4rem, 6vw, 4rem)', fontWeight: 700, lineHeight: 1.1, margin: '0 0 20px' , color: '#0ea5e9'}}>
            Somewhere, a child is waiting<br /><span style={{ color: G }}>for someone like you.</span>
          </h2>
          <p style={{ color: MU, fontSize: 17, lineHeight: 1.7, margin: '0 0 40px' }}>
            58 cents. One child. Every course. A future that looks different because you showed up.
          </p>
          <Link to="/sponsor/give"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 12, padding: '24px 56px', borderRadius: 999, background: G, color: M, fontWeight: 800, fontSize: 20, textDecoration: 'none', boxShadow: `0 10px 50px ${G}66` }}>
            💛 Sponsor a Child Today
          </Link>
          <Link to="/sponsor/corporate?mode=named"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 18, padding: '16px 36px', borderRadius: 999, background: 'transparent', color: G, border: '1.5px solid rgba(212,168,67,0.35)', fontWeight: 700, fontSize: 15, textDecoration: 'none' }}>
            🤝 Sponsor someone you know, hands-on
          </Link>
          <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: 12, margin: '24px 0 0', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.15em' }}>
            You pay half · We match half · Cancel anytime
          </p>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer style={{ borderTop: `1px solid ${BR}`, padding: '40px 24px', display: 'flex', justifyContent: 'center', gap: 32, flexWrap: 'wrap' }}>
        {[
          { l: 'Families', to: '/homeschool' },
          { l: 'Schools', to: '/schools' },
          { l: 'Churches', to: '/church' },
          { l: 'Privacy', to: '/privacy' },
          { l: 'Terms', to: '/terms' },
        ].map(l => (
          <Link key={l.l} to={l.to} style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.2em', textDecoration: 'none' }}>{l.l}</Link>
        ))}
      </footer>
    </div>
  )
}

function FaqItem({ q, a, defaultOpen }: { q: string; a: string; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(!!defaultOpen)
  return (
    <div style={{ marginBottom: 12 }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: '100%', textAlign: 'left', padding: '20px 24px', background: open ? CA : 'transparent', border: `1px solid ${open ? BR : 'rgba(255,255,255,0.06)'}`, borderRadius: 16, cursor: 'pointer', color: TX, fontFamily: sans, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>{q}</span>
        <span style={{ color: G, fontSize: 22, fontWeight: 300, lineHeight: 1 }}>{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div style={{ padding: '16px 24px 4px', color: MU, fontSize: 14, lineHeight: 1.7 }}>{a}</div>
      )}
    </div>
  )
}